const WHOLESALE_PREORDER_STEPPER_MAX = 999995;
/** Internal flag — cart/order templates render trade pre-order lines as “Pre-Order”. */
const WHOLESALE_PREORDER_CART_PROPERTIES = { _wholesale_preorder: "1" };
/** Internal flag — trade in-stock catalogue lines show as “In Stock” in cart/order. */
const WHOLESALE_INSTOCK_CART_PROPERTIES = { _wholesale_in_stock: "1" };

/** @returns {"in-stock"|"pre-order"|"mixed"|null} */
function tradeCatalogKindFromLineProperties(properties) {
  if (!properties || typeof properties !== "object") return null;
  const isPreorder =
    properties._wholesale_preorder != null &&
    String(properties._wholesale_preorder).trim() !== "";
  const isInstock =
    properties._wholesale_in_stock != null &&
    String(properties._wholesale_in_stock).trim() !== "";
  if (isPreorder) return "pre-order";
  if (isInstock) return "in-stock";
  return null;
}

/** @returns {"in-stock"|"pre-order"|"mixed"|null} */
function tradeCatalogKindFromCart(cart) {
  if (!cart?.items?.length) return null;
  let kind = null;
  for (const item of cart.items) {
    const itemKind = tradeCatalogKindFromLineProperties(item.properties);
    if (!itemKind) continue;
    if (kind && kind !== itemKind) return "mixed";
    kind = itemKind;
  }
  return kind;
}

/** @returns {"in-stock"|"pre-order"|"mixed"|null} */
function tradeCatalogKindFromStagedItems(stagedItems) {
  if (!Array.isArray(stagedItems) || stagedItems.length === 0) return null;
  let kind = null;
  for (const item of stagedItems) {
    if (!item?.quantity || Number(item.quantity) <= 0) continue;
    const itemKind = item.preorder ? "pre-order" : "in-stock";
    if (kind && kind !== itemKind) return "mixed";
    kind = itemKind;
  }
  return kind;
}

function tradeCatalogKindsConflict(existingKind, targetKind) {
  if (!existingKind || !targetKind) return false;
  if (existingKind === "mixed" || targetKind === "mixed") return true;
  return existingKind !== targetKind;
}

function readWholesaleTradeLocale(key) {
  const locale = readLiquidWholesaleTradeContext()?.locale;
  if (locale && typeof locale[key] === "string" && locale[key].trim() !== "") {
    return locale[key].trim();
  }
  return "";
}

function readLiquidWholesaleTradeContext() {
  const el = document.getElementById("wholesale-trade-context-json");
  if (!el || !el.textContent) return null;
  try {
    const v = JSON.parse(el.textContent.trim());
    if (v === null || v === undefined) return null;
    if (typeof v === "object" && !Array.isArray(v)) return v;
    const s = String(v).trim();
    if (/^[1-9]\d*$/.test(s)) {
      return { collectionId: s, customerB2b: false };
    }
    return null;
  } catch (_) {
    return null;
  }
}

function rememberTradeCatalogFromWholesaleContext(catalogKind) {
  const ctx = readLiquidWholesaleTradeContext();
  const kind = catalogKind || ctx?.catalogKind;
  if (kind !== "in-stock" && kind !== "pre-order") return;
  const url =
    kind === "pre-order" ? ctx?.catalogUrls?.preOrder : ctx?.catalogUrls?.inStock;
  window.TradeCatalogMemory?.remember(kind, url);
}

document.addEventListener("DOMContentLoaded", function () {
  /** Globo / PDP-aligned line markers (exact strings from working form POST). */
  const WHOLESALE_GLOBO_PREORDER_LABEL = "Pre-order item";

  function wholesaleInputForVariant(variantId) {
    return document.querySelector(
      `[data-wholesale-variant="${variantId}"]`,
    );
  }

  /** Merges theme trade labels + Globo properties for pre-order rows. */
  function wholesaleTradeLineProperties(item, input) {
    let preorder =
      typeof item.preorder === "boolean" ? item.preorder : undefined;
    if (typeof preorder !== "boolean") {
      preorder = input?.dataset?.wholesalePreorder === "true";
    }
    const props = preorder
      ? { ...WHOLESALE_PREORDER_CART_PROPERTIES }
      : { ...WHOLESALE_INSTOCK_CART_PROPERTIES };

    if (preorder) {
      props["_is_preorder"] = WHOLESALE_GLOBO_PREORDER_LABEL;
      props["_preorder_locale"] =
        item.preorderLocale ||
        input?.dataset?.wholesalePreorderLocale ||
        "en";
    }
    return props;
  }

  /** Shopify B2B checkout rejects selling_plan on cart lines; pre-orders use line properties instead. */
  function isWholesaleB2bCustomer() {
    return readLiquidWholesaleTradeContext()?.customerB2b === true;
  }

  function wholesaleSellingPlanForLine(item, input) {
    if (isWholesaleB2bCustomer()) return null;
    const raw = item.sellingPlan ?? input?.dataset?.wholesaleSellingPlan;
    if (raw === undefined || raw === null || raw === "") return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  function globoLocaleFromParams(params) {
    if (typeof params?.locale === "string" && params.locale.trim() !== "") {
      return params.locale.trim();
    }
    return "en";
  }

  function firstSellingPlanIdFromGloboAllocations(allocations) {
    if (!allocations || !allocations.length) return null;
    const first = allocations[0];
    const raw =
      first.selling_plan_id ??
      (first.selling_plan && first.selling_plan.id) ??
      null;
    if (raw === undefined || raw === null || raw === "") return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  /**
   * Globo maps collection id → preorder profile → selling_plan_ids (often before products[] is filled).
   */
  function globoSellingPlanFromPreorderCampaign(tradeCollectionId, params) {
    if (
      tradeCollectionId === undefined ||
      tradeCollectionId === null ||
      String(tradeCollectionId).trim() === ""
    ) {
      return null;
    }

    const ps = params?.preorderSettings;
    if (!ps) return null;

    const cid = String(tradeCollectionId);
    const collectionMap =
      typeof ps.collections === "object" &&
      ps.collections !== null &&
      !Array.isArray(ps.collections)
        ? ps.collections
        : null;
    if (!collectionMap) return null;

    let profileRef =
      collectionMap[cid] ??
      (Number.isFinite(Number(cid)) ? collectionMap[Number(cid)] : null);
    if (profileRef == null || profileRef === "") return null;

    const profiles =
      typeof ps.profiles === "object" &&
      ps.profiles !== null &&
      !Array.isArray(ps.profiles)
        ? ps.profiles
        : null;
    if (!profiles) return null;

    const profile =
      profiles[profileRef] ?? profiles[String(profileRef)] ?? null;
    if (!profile) return null;

    let planIds = profile.selling_plan_ids;
    if (typeof planIds === "string") {
      try {
        planIds = JSON.parse(planIds);
      } catch (_) {
        planIds = null;
      }
    }
    if (!planIds || !planIds.length) return null;

    const planIdNum = Number(planIds[0]);
    if (!Number.isFinite(planIdNum) || planIdNum <= 0) return null;

    return { sellingPlanId: planIdNum, locale: globoLocaleFromParams(params) };
  }

  /**
   * Prefer variant match in GloboPreorderParams.products; else collection campaign (preorderSettings).
   */
  function globoSellingPlanLocaleForVariant(variantId, tradeCollectionId) {
    const vid = variantId === undefined || variantId === null ? "" : String(variantId);
    if (!vid) return null;

    const params =
      typeof window !== "undefined" ? window.GloboPreorderParams : undefined;
    if (!params) return null;

    const localeFallback = globoLocaleFromParams(params);

    if (Array.isArray(params.products) && params.products.length) {
      for (let pi = 0; pi < params.products.length; pi++) {
        const product = params.products[pi];
        const variants =
          Array.isArray(product?.variants) && product.variants.length
            ? product.variants
            : [];
        for (let vi = 0; vi < variants.length; vi++) {
          const v = variants[vi];
          if (String(v?.id) !== vid) continue;
          const planId = firstSellingPlanIdFromGloboAllocations(
            v.selling_plan_allocations,
          );
          if (planId != null) {
            return { sellingPlanId: planId, locale: localeFallback };
          }
        }
      }
    }

    return globoSellingPlanFromPreorderCampaign(tradeCollectionId, params);
  }

  function readLiquidWholesaleTradeCollectionJson() {
    const ctx = readLiquidWholesaleTradeContext();
    if (!ctx) return "";
    const raw = ctx.collectionId ?? ctx.collection_id;
    if (raw === null || raw === undefined || raw === "") return "";
    const s = String(raw).trim();
    return /^[1-9]\d*$/.test(s) ? s : "";
  }

  /** Resolves Shopify collection id for Globo preorderSettings.collections (Liquid attr, then Globo’s own field). */
  function tradeCollectionIdForGloboCampaign() {
    let id = readLiquidWholesaleTradeCollectionJson();

    const stickyRaw = document
      .querySelector(".wholesale-sticky-tab[data-wholesale-collection-id]")
      ?.getAttribute?.("data-wholesale-collection-id");
    if (!id && stickyRaw != null && String(stickyRaw).trim() !== "") {
      id = String(stickyRaw).trim();
    }

    if (!id) {
      const firstPreorder = document.querySelector(
        'input[data-wholesale-preorder="true"]',
      );
      const wrap =
        firstPreorder?.closest(
          ".trade-catalog-section-wrap[data-trade-collection-id]",
        ) ??
        document.querySelector(
          ".trade-catalog-section-wrap[data-trade-collection-id]",
        );

      id = wrap?.getAttribute("data-trade-collection-id")?.trim() ?? "";
    }

    const globoPc = window.GloboPreorderParams?.collectionId;
    if (
      !id &&
      globoPc !== undefined &&
      globoPc !== null &&
      String(globoPc).trim() !== ""
    ) {
      id = String(globoPc).trim();
    }

    /** Globo preorderSettings.collections keys match Shopify collection IDs; use sole key when unambiguous. */
    if (!id) {
      const collections =
        window.GloboPreorderParams?.preorderSettings?.collections;
      if (
        collections &&
        typeof collections === "object" &&
        !Array.isArray(collections)
      ) {
        const keys = Object.keys(collections);
        if (keys.length === 1 && String(keys[0]).trim() !== "") {
          id = String(keys[0]).trim();
        }
      }
    }

    return id || null;
  }

  /** Console: wholesaleGloboCampaignDebug(). Registered early so init errors don’t hide it. */
  window.wholesaleGloboCampaignDebug = function wholesaleGloboCampaignDebug() {
    const liquidContext = readLiquidWholesaleTradeContext();
    const firstVid = document.querySelector(
      'input[data-wholesale-preorder="true"][data-wholesale-variant]',
    )?.getAttribute("data-wholesale-variant");
    const tcid = tradeCollectionIdForGloboCampaign();
    return {
      liquidJsonRaw: document.getElementById("wholesale-trade-context-json")
        ?.textContent?.trim?.(),
      liquidJsonParsed: liquidContext,
      customerB2b: isWholesaleB2bCustomer(),
      skipsSellingPlanOnCartAdd: isWholesaleB2bCustomer(),
      stickyCollectionAttr: document
        .querySelector(".wholesale-sticky-tab[data-wholesale-collection-id]")
        ?.getAttribute("data-wholesale-collection-id"),
      stickyBarExists:
        !!document.querySelector(".wholesale-sticky-tab"),
      tradeCollectionId: tcid,
      globoParamsCollectionId: window.GloboPreorderParams?.collectionId,
      wrapAttr: document
        .querySelector(".trade-catalog-section-wrap[data-trade-collection-id]")
        ?.getAttribute("data-trade-collection-id"),
      globoPreorderCollectionKeys:
        typeof window.GloboPreorderParams?.preorderSettings?.collections === "object"
          ? Object.keys(
              window.GloboPreorderParams.preorderSettings.collections || {},
            )
          : [],
      samplePlanForFirstPreorderVariant:
        firstVid != null && String(firstVid).trim() !== ""
          ? globoSellingPlanLocaleForVariant(firstVid, tcid)
          : null,
    };
  };

  /** Sets data-wholesale-selling-plan / locale on preorder trade inputs when Liquid left them blank. */
  function applyGloboSellingPlansToWholesaleInputs() {
    const tradeCollectionId = tradeCollectionIdForGloboCampaign();

    document.querySelectorAll("[data-wholesale-variant]").forEach((input) => {
      if (input.dataset.wholesalePreorder !== "true") return;
      if (input.dataset.wholesaleSellingPlan) return;
      const variantId = input.dataset.wholesaleVariant;
      const match = globoSellingPlanLocaleForVariant(variantId, tradeCollectionId);
      if (!match) return;
      input.setAttribute(
        "data-wholesale-selling-plan",
        String(match.sellingPlanId),
      );
      input.setAttribute(
        "data-wholesale-preorder-locale",
        match.locale || "en",
      );
    });

    let mutated = false;
    items.forEach((item) => {
      const inp = wholesaleInputForVariant(item.id);
      if (!inp || inp.dataset.wholesalePreorder !== "true") return;
      item.preorder = true;
      const sp = inp.dataset.wholesaleSellingPlan;
      if (!sp) return;
      if (item.sellingPlan !== sp) mutated = true;
      item.sellingPlan = sp;
      const loc = inp.dataset.wholesalePreorderLocale || "en";
      if (item.preorderLocale !== loc) mutated = true;
      item.preorderLocale = loc;
    });
    if (mutated) {
      localStorage.setItem("wholesale-items", JSON.stringify(items));
    }
  }

  const globoHydrationDelaysMs = [
    0, 80, 200, 500, 1200, 3000, 6000, 10000, 15000, 20000,
  ];

  function scheduleGloboSellingPlanHydration(options) {
    const full = !(options && options.quick);
    const apply = applyGloboSellingPlansToWholesaleInputs;
    if (full) {
      globoHydrationDelaysMs.forEach((ms) => setTimeout(apply, ms));
    } else {
      apply();
      setTimeout(apply, 600);
      setTimeout(apply, 2500);
    }
  }

  //
  //
  const totalElement = document.querySelector("[data-wholesale-total]");
  const pairCountElement = document.querySelector("[data-wholesale-pair-count]");
  const addToCart = document.querySelector("[data-wholesale-add-to-cart]");
  const clearButton = document.querySelector("[data-wholesale-clear]");
  const stickyTab = document.querySelector(".wholesale-sticky-tab");
  let wholesaleInteractiveReady = !!(totalElement && addToCart && stickyTab);
  if (!wholesaleInteractiveReady) {
    console.warn(
      "[wholesale.js] Wholesale UI markup missing (`data-wholesale-total`, `[data-wholesale-add-to-cart]`, or `.wholesale-sticky-tab`). Globo hydrate may still patch preorder inputs.",
    );
  }

  let runningTotal = 0;
  let items = JSON.parse(localStorage.getItem("wholesale-items")) || [];

  /** Globo embed can initialise after DOMContentLoaded; re-run once the window has fully loaded. */
  window.addEventListener("load", function () {
    applyGloboSellingPlansToWholesaleInputs();
    setTimeout(applyGloboSellingPlansToWholesaleInputs, 500);
    setTimeout(applyGloboSellingPlansToWholesaleInputs, 2000);
  });

  scheduleGloboSellingPlanHydration({ quick: false });
  rememberTradeCatalogFromWholesaleContext();

  function mixedCatalogErrorBody(locale) {
    return (
      locale?.mixedCartBasket ||
      locale?.mixedCartSwitch ||
      locale?.mixedStagedSwitch ||
      "You can't mix in-stock and pre-order items in one basket. Please either remove selections, empty your basket or complete your current order first."
    );
  }

  function catalogSwitchErrorTitleForKind(targetKind, locale) {
    if (targetKind === "pre-order") {
      return (
        locale?.catalogSwitchErrorTitlePreOrder || "Can't switch to Pre-Order"
      );
    }
    if (targetKind === "in-stock") {
      return (
        locale?.catalogSwitchErrorTitleInStock || "Can't switch to In-Stock"
      );
    }
    return locale?.catalogSwitchErrorTitle || "Can't switch catalogue";
  }

  function showCatalogSwitchError(message, targetKind) {
    if (!message && targetKind == null) {
      window.TradeStickyBar?.clearError();
      return;
    }
    const locale = readLiquidWholesaleTradeContext()?.locale;
    window.TradeStickyBar?.showError(message || mixedCatalogErrorBody(locale), {
      title: catalogSwitchErrorTitleForKind(targetKind, locale),
    });
  }

  function showWholesaleBasketError(message, options) {
    if (!message) {
      window.TradeStickyBar?.clearError();
      return;
    }
    window.TradeStickyBar?.showError(message, {
      title:
        (options && options.title) ||
        readWholesaleTradeLocale("basketErrorTitle") ||
        "Can't add to basket",
    });
  }

  async function fetchCartCatalogKind() {
    try {
      const cart = await fetch(
        (window.Shopify?.routes?.root || "/") + "cart.js",
      ).then((res) => res.json());
      return tradeCatalogKindFromCart(cart);
    } catch (_) {
      return null;
    }
  }

  function bindCatalogToggleGuards(stagedItemsRef) {
    document
      .querySelectorAll("[data-collection-catalog-switcher]")
      .forEach((nav) => {
        nav.addEventListener("click", async (event) => {
          const link = event.target.closest(
            "a.collection-catalog-segmented__segment:not(.collection-catalog-segmented__segment--current)",
          );
          if (!link) return;

          showCatalogSwitchError("");
          const targetKind = link.getAttribute("data-catalog-target");
          if (targetKind !== "in-stock" && targetKind !== "pre-order") return;

          const stagedKind = tradeCatalogKindFromStagedItems(stagedItemsRef);
          if (tradeCatalogKindsConflict(stagedKind, targetKind)) {
            event.preventDefault();
            showCatalogSwitchError(null, targetKind);
            return;
          }

          const cartKind = await fetchCartCatalogKind();
          if (tradeCatalogKindsConflict(cartKind, targetKind)) {
            event.preventDefault();
            showCatalogSwitchError(null, targetKind);
          }
        });
      });
  }

  function syncSelectionBarCatalogTheme() {
    if (!addToCart) return;
    const kind =
      readLiquidWholesaleTradeContext()?.catalogKind ||
      stickyTab?.dataset?.tradeCatalog ||
      document.querySelector(".trade-catalog-section-wrap")?.dataset
        ?.tradeCatalog ||
      "in-stock";
    addToCart.classList.remove("wholesale-add--in-stock", "wholesale-add--pre-order");
    if (kind === "pre-order") {
      addToCart.classList.add("wholesale-add--pre-order");
    } else {
      addToCart.classList.add("wholesale-add--in-stock");
    }
  }

  function syncStickyActions() {
    const hasItems = items.some((item) => Number(item.quantity) > 0);
    if (addToCart) addToCart.disabled = !hasItems;
    if (clearButton) clearButton.disabled = !hasItems;
  }

  if (wholesaleInteractiveReady) {
    updateTotal();
    populateQuantities();
    setEventListeners();
    syncSelectionBarCatalogTheme();
    syncStickyActions();
  }

  bindCatalogToggleGuards(items);
  applyB2bCatalogToggleHrefs();

  function applyB2bCatalogToggleHrefs() {
    const ctx =
      readLiquidWholesaleTradeContext() ||
      (function () {
        const el = document.getElementById("trade-b2b-location-context-json");
        if (!el || !el.textContent) return null;
        try {
          return JSON.parse(el.textContent.trim());
        } catch (_) {
          return null;
        }
      })();
    const locationSwitch = ctx?.locationSwitch;
    if (!locationSwitch) return;

    const currentName = ctx.currentLocationName || "";

    const stockLink = document.querySelector(
      '[data-collection-catalog-switcher] [data-catalog-target="in-stock"]',
    );
    const poLink = document.querySelector(
      '[data-collection-catalog-switcher] [data-catalog-target="pre-order"]',
    );

    if (
      stockLink &&
      locationSwitch.inStock?.setUrl &&
      locationSwitch.inStock.name &&
      currentName !== locationSwitch.inStock.name
    ) {
      stockLink.href = locationSwitch.inStock.setUrl;
    }

    if (
      poLink &&
      locationSwitch.preOrder?.setUrl &&
      locationSwitch.preOrder.name &&
      currentName !== locationSwitch.preOrder.name
    ) {
      poLink.href = locationSwitch.preOrder.setUrl;
    }
  }

  // add event listener for the collection:updated event for when facets update the filters
  document.addEventListener("collection:updated", function () {
    if (wholesaleInteractiveReady) {
      populateQuantities();
      setEventListeners();
    }
    scheduleGloboSellingPlanHydration({ quick: true });
  });

  async function refreshTradeCatalogStateAfterCartChange() {
    window.TradeStickyBar?.clearError();
    showCatalogSwitchError("");
    showWholesaleBasketError("");

    if (wholesaleInteractiveReady) {
      await populateQuantities();
    }
  }

  // add event listener for the cart-updated event for when item qantities are updated in the cart
  document.addEventListener("cart-updated", function () {
    refreshTradeCatalogStateAfterCartChange();
  });
  //
  //
  // clear button
  clearButton?.addEventListener("click", function () {
    items = [];
    localStorage.removeItem("wholesale-items");
    updateTotal();
    populateQuantities();
    showWholesaleBasketError("");
    syncStickyActions();
  });
  function syncWholesaleStepper(input) {
    if (!input) return;
    const minus = input.previousElementSibling;
    const plus = input.nextElementSibling;
    if (!minus || !plus) return;
    const val = Number(input.value) || 0;
    const max = Number(input.max);
    if (val <= 0) {
      minus.classList.add("disabled");
      minus.disabled = true;
    } else {
      minus.classList.remove("disabled");
      minus.disabled = false;
    }
    // Disable + only when another step of 5 would exceed max (not when it lands exactly on max)
    if (val + 5 > max) {
      plus.classList.add("disabled");
      plus.disabled = true;
    } else {
      plus.classList.remove("disabled");
      plus.disabled = false;
    }
  }

  // if items is not empty, update the dom to show the items
  async function populateQuantities() {
    if (items.length > 0) {
      items.forEach((item) => {
        const input = wholesaleInputForVariant(item.id);
        if (input) input.value = item.quantity;
      });
    } else {
      document.querySelectorAll("[data-wholesale-variant]").forEach((input) => {
        input.value = 0;
      });
    }

    await fetch((window.Shopify?.routes?.root || "/") + "cart.js")
      .then((res) => res.json())
      .then((data) => {
        const cartVariantIds = new Set(
          (data.items || []).map((cartItem) => cartItem.variant_id),
        );

        document.querySelectorAll("[data-wholesale-variant]").forEach((input) => {
          const variantId = Number(input.dataset.wholesaleVariant);
          if (cartVariantIds.has(variantId)) return;

          const stocked = Number(input.dataset.wholesaleInventory) || 0;
          const isPreorderTrade = input.dataset.wholesalePreorder === "true";
          const maxQty = isPreorderTrade ? WHOLESALE_PREORDER_STEPPER_MAX : stocked;
          input.setAttribute("max", String(maxQty));

          const cartQuantityMessageElement = document.querySelector(
            `[data-cart-quantity-variant="${variantId}"]`,
          );
          if (cartQuantityMessageElement) {
            cartQuantityMessageElement.textContent = "";
          }
        });

        data.items.forEach((cartItem) => {
          const input = wholesaleInputForVariant(cartItem.variant_id);
          if (!input) return;

          const stocked = Number(input.dataset.wholesaleInventory) || 0;
          const isPreorderTrade = input.dataset.wholesalePreorder === "true";
          const maxQty = isPreorderTrade
            ? Math.max(
                0,
                WHOLESALE_PREORDER_STEPPER_MAX - cartItem.quantity,
              )
            : stocked - cartItem.quantity;
          input.setAttribute("max", String(maxQty));

          const cartQuantityMessageElement = document.querySelector(
            `[data-cart-quantity-variant="${cartItem.variant_id}"]`
          );
          if (cartQuantityMessageElement) {
            cartQuantityMessageElement.textContent = isPreorderTrade
              ? `In cart: ${cartItem.quantity} (pre-order)`
              : `In cart: ${cartItem.quantity} of ${stocked}`;
          }
        });

        document
          .querySelectorAll("[data-wholesale-variant]")
          .forEach(syncWholesaleStepper);
      })
      .catch(() => {
        document
          .querySelectorAll("[data-wholesale-variant]")
          .forEach(syncWholesaleStepper);
      });
  }

  //
  //
  function setEventListeners() {
    // remove the event listeners from the old products
    document
      .querySelectorAll("[data-wholesale-qty-adjust]")
      .forEach((button) => {
        button.removeEventListener("click", adjustQuantity);
      });
    // get all the adjust buttons
    const newQtyAdjustbuttons = document.querySelectorAll(
      "[data-wholesale-qty-adjust]"
    );
    // add event listeners to the new buttons
    newQtyAdjustbuttons.forEach((button) => {
      button.addEventListener("click", adjustQuantity);
    });
  }

  //
  //
  // add event listener for the infinite scroll products added event
  document.addEventListener("infiniteScroll:productsAdded", function (event) {
    if (wholesaleInteractiveReady) {
      populateQuantities();
      setEventListeners();
    }
    scheduleGloboSellingPlanHydration({ quick: true });
  });

  //
  //
  function adjustQuantity(event) {
    showWholesaleBasketError("");
    //
    const button = event.currentTarget;
    const qtyAdjust = button.dataset.wholesaleQtyAdjust;
    const input = button.closest(".product-quantity").querySelector("input");
    const variantId = input.dataset.wholesaleVariant;
    const maxQuantity = Number(input.max);
    const price = input.dataset.wholesalePrice;

    // Find or create item
    let currentItem = items.find((item) => item.id === variantId);

    if (!currentItem) {
      currentItem = {
        id: variantId,
        quantity: 0,
        price,
        preorder: input.dataset.wholesalePreorder === "true",
      };
      if (currentItem.preorder && input.dataset.wholesaleSellingPlan) {
        currentItem.sellingPlan = input.dataset.wholesaleSellingPlan;
        currentItem.preorderLocale = input.dataset.wholesalePreorderLocale || "";
      }
      items.push(currentItem);
    } else {
      currentItem.preorder = input.dataset.wholesalePreorder === "true";
      if (currentItem.preorder && input.dataset.wholesaleSellingPlan) {
        currentItem.sellingPlan = input.dataset.wholesaleSellingPlan;
        currentItem.preorderLocale = input.dataset.wholesalePreorderLocale || "";
      } else if (!currentItem.preorder) {
        delete currentItem.sellingPlan;
        delete currentItem.preorderLocale;
      }
    }
    // Calculate new quantity
    const quantityChange = qtyAdjust === "plus" ? 5 : -5;
    const proposedQuantity = currentItem.quantity + quantityChange;

    // Validate and apply quantity change
    if (proposedQuantity <= 0) {
      // Remove item if quantity would be 0 or negative
      items = items.filter((item) => item.id !== variantId);
      input.value = 0;
    } else if (proposedQuantity <= maxQuantity) {
      const incomingKind =
        input.dataset.wholesalePreorder === "true" ? "pre-order" : "in-stock";
      const otherStaged = items.filter(
        (item) => item.id !== variantId && Number(item.quantity) > 0,
      );
      const stagedKind = tradeCatalogKindFromStagedItems(otherStaged);
      if (
        quantityChange > 0 &&
        tradeCatalogKindsConflict(stagedKind, incomingKind)
      ) {
        showWholesaleBasketError(mixedCatalogErrorBody(readLiquidWholesaleTradeContext()?.locale));
        return;
      }

      // Update quantity if within bounds
      currentItem.quantity = proposedQuantity;
      input.value = proposedQuantity;
    }

    syncWholesaleStepper(input);

    updateTotal();
    localStorage.setItem("wholesale-items", JSON.stringify(items));
    syncStickyActions();
  }

  //
  //
  function updateTotal() {
    const pairCount = items.reduce(
      (acc, item) => acc + (Number(item.quantity) || 0),
      0,
    );
    if (pairCountElement) {
      pairCountElement.textContent = String(pairCount);
    }
    if (!totalElement) return;
    runningTotal =
      items.length === 0
        ? 0
        : items.reduce(
            (acc, item) => acc + item.price * item.quantity,
            0,
          );
    totalElement.textContent = formatMoney(runningTotal);
  }

  //
  //
  // add to cart
  addToCart?.addEventListener("click", async function (event) {
    if (items.length === 0) return;
    applyGloboSellingPlansToWholesaleInputs();

    const thisButton = event.currentTarget;
    showWholesaleBasketError("");
    showCatalogSwitchError("");

    const addingKind = tradeCatalogKindFromStagedItems(items);
    if (addingKind === "mixed") {
      showWholesaleBasketError(mixedCatalogErrorBody(readLiquidWholesaleTradeContext()?.locale));
      return;
    }

    const cartKind = await fetchCartCatalogKind();
    if (tradeCatalogKindsConflict(cartKind, addingKind)) {
      showWholesaleBasketError(mixedCatalogErrorBody(readLiquidWholesaleTradeContext()?.locale));
      return;
    }

    rememberTradeCatalogFromWholesaleContext(addingKind);

    const formData = {
      items: items.map((item) => {
        const input = wholesaleInputForVariant(item.id);
        const line = {
          id: Number(item.id),
          quantity: Number(item.quantity),
          properties: wholesaleTradeLineProperties(item, input),
        };
        const sp = wholesaleSellingPlanForLine(item, input);
        if (sp != null) {
          line.selling_plan = sp;
        }
        return line;
      }),
    };

    thisButton.disabled = true;

    fetch((window.Shopify?.routes?.root || "/") + "cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(formData),
    })
      .then((res) => res.json())
      .then((result) => {
        if (result.status === 422) {
          const msg =
            typeof result.description === "string"
              ? result.description
              : typeof result.message === "string"
                ? result.message
                : "";
          showWholesaleBasketError(
            msg || "Could not add selections to basket. Please try again.",
          );
          thisButton.disabled = false;
          return;
        }

        items = [];
        localStorage.removeItem("wholesale-items");
        updateTotal();
        showWholesaleBasketError("");
        syncStickyActions();

        if (typeof window.refreshCart === "function") {
          window.refreshCart();
        } else {
          document.dispatchEvent(new CustomEvent("cart-updated"));
          document.getElementById("site-cart-sidebar")?.show?.();
        }

        populateQuantities();
        thisButton.disabled = false;
      })
      .catch((err) => {
        console.log("there was an error attempting to add to cart", err);
        thisButton.disabled = false;
        const fallback =
          typeof KROWN !== "undefined"
            ? KROWN.settings.locales.cart_general_error
            : "";
        showWholesaleBasketError(
          fallback || "Could not add selections to basket. Please try again.",
        );
      });

  });

});

/** Resolves to `shop.money_format` from Liquid via `KROWN.settings.shop_money_format`. */
function wholesaleMoneyFormat() {
  if (
    typeof KROWN !== "undefined" &&
    KROWN.settings &&
    typeof KROWN.settings.shop_money_format === "string" &&
    KROWN.settings.shop_money_format.trim() !== ""
  ) {
    return KROWN.settings.shop_money_format;
  }
  return "{{amount}}";
}

const formatMoney = function (cents, format) {
  if (typeof cents == "string") {
    cents = cents.replace(".", "");
  }
  let value = "";
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString =
    typeof format === "string" && format.trim() !== ""
      ? format
      : wholesaleMoneyFormat();

  function formatWithDelimiters(number, precision, thousands, decimal) {
    thousands = thousands || ",";
    decimal = decimal || ".";

    if (isNaN(number) || number == null) {
      return 0;
    }

    number = (number / 100.0).toFixed(precision);

    const parts = number.split(".");
    const dollarsAmount = parts[0].replace(
      /(\d)(?=(\d\d\d)+(?!\d))/g,
      "$1" + thousands
    );
    const centsAmount = parts[1] ? decimal + parts[1] : "";

    return dollarsAmount + centsAmount;
  }

  const ph = formatString.match(placeholderRegex);
  if (!ph) {
    return formatString;
  }

  switch (ph[1]) {
    case "amount":
      value = formatWithDelimiters(cents, 2);
      break;
    case "amount_no_decimals":
      value = formatWithDelimiters(cents, 0);
      break;
    case "amount_with_comma_separator":
      value = formatWithDelimiters(cents, 2, ".", ",");
      break;
    case "amount_no_decimals_with_comma_separator":
      value = formatWithDelimiters(cents, 0, ".", ",");
      break;
    case "amount_no_decimals_with_space_separator":
      value = formatWithDelimiters(cents, 0, " ");
      break;
    case "amount_with_apostrophe_separator":
      value = formatWithDelimiters(cents, 2, "'", ".");
      break;
  }

  return formatString.replace(placeholderRegex, value);
};
